import { useState, useCallback, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, useMotionValue, useSpring, animate } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useSliderKeyboard } from '@/hooks/useSliderKeyboard'
import { haptic } from '@/lib/haptic'
import SliderSwipePrompt from '@/components/SliderSwipePrompt'

const workItems = [
  {
    client: 'The Book Incubator',
    image: '/assets/book-incubator.png',
    industry: 'Professional Services',
    project: 'Digital Platform & Strategy',
    results: ['Platform development', 'Growth strategy'],
    description: 'Digital platform and marketing strategy for author development and publishing services.'
  },
  {
    client: 'Dons Whisky',
    image: '/assets/dons-whisky.png',
    industry: 'Food & Beverage',
    project: 'Brand Identity (Proposed)',
    results: ['Premium brand positioning'],
    description: 'Complete brand identity and e-commerce platform for premium whisky distillery.'
  },
  {
    client: 'Forkin Perth',
    image: '/assets/forkin-perth.png',
    industry: 'Social Media',
    project: 'Local Food Guide',
    results: ['Growth strategy', 'Platform development'],
    description: 'Comprehensive digital marketing strategy and local food guide.'
  },
  {
    client: 'The Pet Practice',
    image: '/assets/pet-practice.png',
    industry: 'Professional Services',
    project: 'Digital Marketing Strategy',
    results: ['Digital strategy', 'Performance marketing'],
    description: 'Digital marketing strategy for a local veterinary practice.'
  },
  {
    client: 'Ram Guard',
    image: '/assets/ram-guard.png',
    industry: 'Professional Services',
    project: 'Brand Identity (Proposed)',
    results: ['Professional brand identity'],
    description: 'Proposed brand identity and digital presence for a local security and protection services.'
  },
  {
    client: 'Smol Bakery',
    image: '/assets/smol-bakery.png',
    industry: 'Food & Beverage',
    project: 'Ecommerce Strategy',
    results: ['Ecommerce strategy', 'Online growth'],
    description: 'Ecommerce strategy for an artisan bakery, covering online sales, product positioning, and growth.'
  },
  {
    client: 'Vibe Group',
    image: '/assets/vibe-group.png',
    industry: 'Information Technology',
    project: 'Web Development',
    results: ['CMS web development'],
    description: 'Web development for Melbourne based IT services company.'
  },
]

const GRID_MAX_ITEMS = 9

const allItems = [
  ...workItems.map((item) => ({ type: 'card' as const, item })),
  { type: 'placeholder' as const },
]

const placeholderCountForGrid = Math.max(0, GRID_MAX_ITEMS - workItems.length)
const allItemsForGrid = [
  ...workItems.map((item) => ({ type: 'card' as const, item })),
  ...Array.from({ length: placeholderCountForGrid }, () => ({ type: 'placeholder' as const })),
]

const TILT_MAX = 12

function WorkCard({ item, index, flippedIndex, onFlip, clickDisabled, disableTilt }: { item: typeof workItems[0]; index: number; flippedIndex: number | null; onFlip: (i: number | null) => void; clickDisabled?: boolean; disableTilt?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const tiltX = useMotionValue(0)
  const tiltY = useMotionValue(0)
  const smoothTiltX = useSpring(tiltX, { stiffness: 400, damping: 30 })
  const smoothTiltY = useSpring(tiltY, { stiffness: 400, damping: 30 })

  const isActive = isHovered || isFocused

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (disableTilt) return
    setIsHovered(true)
    const rect = ref.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2
    tiltX.set(x * TILT_MAX)
    tiltY.set(-y * TILT_MAX)
  }, [disableTilt, tiltX, tiltY])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
    tiltX.set(0)
    tiltY.set(0)
  }, [tiltX, tiltY])

  return (
    <motion.div
      ref={ref}
      tabIndex={0}
      role="button"
      aria-pressed={flippedIndex === index}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !clickDisabled) {
          e.preventDefault()
          haptic()
          onFlip(flippedIndex === index ? null : index)
        }
      }}
      className="aspect-square cursor-pointer perspective-1000 flex-shrink-0 w-full h-full outline-none focus:outline-none select-none"
      animate={{ scale: disableTilt ? 1 : (isActive ? 1 : 0.95) }}
      transition={{ scale: { duration: 0.3, ease: 'easeInOut' } }}
      style={{
        rotateX: disableTilt ? 0 : smoothTiltY,
        rotateY: disableTilt ? 0 : smoothTiltX,
        transformPerspective: 1000,
        willChange: disableTilt ? 'auto' : 'transform',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          opacity: { duration: 0.6, delay: index * 0.05 },
          y: { duration: 0.6, delay: index * 0.05 },
          rotateY: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
        }}
        className="relative w-full h-full"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: flippedIndex === index ? 180 : 0 }}
        onClick={() => {
          if (!clickDisabled) {
            haptic()
            onFlip(flippedIndex === index ? null : index)
          }
        }}
      >
        <div
          className="absolute inset-0 rounded-lg overflow-hidden shadow-md backface-hidden"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(0deg)' }}
        >
          <img src={item.image} alt={item.client} className="w-full h-full object-cover" />
        </div>
        <div
          className="absolute inset-0 rounded-lg overflow-hidden shadow-md bg-accent p-8 flex flex-col justify-center backface-hidden"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <p className="text-white/80 text-xs font-bold uppercase tracking-widest mb-2">{item.industry}</p>
          <h3 className="text-2xl font-bold font-heading text-white mb-2 uppercase leading-tight">{item.client}</h3>
          <p className="text-white/90 text-sm mb-6">{item.project}</p>
          <div className="space-y-2">
            {item.results.map((result, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs text-white/90">
                <span className="w-1 h-1 bg-white rounded-full flex-shrink-0" />
                {result}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

const AUTOPLAY_INTERVAL_MS = 3000
const AUTOPLAY_PAUSE_AFTER_INTERACTION_MS = 5000

export default function Work() {
  const [flippedIndex, setFlippedIndex] = useState<number | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slideWidth, setSlideWidth] = useState(0)
  const [slidesPerView, setSlidesPerView] = useState(1)
  const [justDragged, setJustDragged] = useState(false)
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const isDraggingRef = useRef(false)
  const flippedIndexRef = useRef<number | null>(null)
  const pauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  flippedIndexRef.current = flippedIndex

  const goToSlide = useCallback((index: number) => {
    const maxSlide = Math.max(0, allItems.length - slidesPerView)
    setCurrentSlide(Math.max(0, Math.min(index, maxSlide)))
  }, [slidesPerView])

  const goPrev = useCallback(() => goToSlide(currentSlide - 1), [currentSlide, goToSlide])
  const goNext = useCallback(() => goToSlide(currentSlide + 1), [currentSlide, goToSlide])

  // Update slides per view and container width on resize
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth
      if (w >= 1024) {
        setSlidesPerView(4)
      } else if (w >= 768) {
        setSlidesPerView(2)
      } else {
        setSlidesPerView(1)
      }
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // Measure container width for pixel-based drag (same as Services)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? container.offsetWidth
      setSlideWidth(width)
    })
    observer.observe(container)
    setSlideWidth(container.offsetWidth)
    return () => observer.disconnect()
  }, [])

  const effectiveSlidesPerView = slidesPerView >= 4 ? 1 : slidesPerView
  const maxSlide = Math.max(0, allItems.length - effectiveSlidesPerView)
  const itemWidth = slideWidth > 0 ? slideWidth / effectiveSlidesPerView : 0

  useEffect(() => {
    if (!isDraggingRef.current && itemWidth > 0 && slidesPerView < 4) {
      animate(x, -currentSlide * itemWidth, { type: 'spring', stiffness: 300, damping: 30 })
    }
  }, [currentSlide, itemWidth, x, slidesPerView])

  const handleDragEnd = useCallback((_: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number }; velocity: { x: number } }) => {
    isDraggingRef.current = false
    setJustDragged(true)
    setTimeout(() => setJustDragged(false), 150)
    if (itemWidth === 0 || slidesPerView >= 4) return
    const threshold = itemWidth * 0.15
    const velocityThreshold = 500
    const shouldGoPrev = info.offset.x > threshold || info.velocity.x > velocityThreshold
    const shouldGoNext = info.offset.x < -threshold || info.velocity.x < -velocityThreshold
    if (shouldGoPrev) goToSlide(currentSlide - 1)
    else if (shouldGoNext) goToSlide(currentSlide + 1)
  }, [currentSlide, goToSlide, itemWidth, slidesPerView])

  useSliderKeyboard(containerRef, goPrev, goNext, slidesPerView < 4)

  // Pause autoplay when card is flipped
  useEffect(() => {
    setIsAutoplayPaused(flippedIndex !== null)
  }, [flippedIndex])

  const pauseAutoplayTemporarily = useCallback(() => {
    setIsAutoplayPaused(true)
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
    pauseTimeoutRef.current = setTimeout(() => {
      if (flippedIndexRef.current === null) setIsAutoplayPaused(false)
      pauseTimeoutRef.current = null
    }, AUTOPLAY_PAUSE_AFTER_INTERACTION_MS)
  }, [])

  // Clear pause timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
    }
  }, [])

  // Autoplay slider every 3 seconds (mobile/tablet only, when not paused)
  useEffect(() => {
    if (slidesPerView >= 4 || isAutoplayPaused) return
    const maxSlide = Math.max(0, allItems.length - (slidesPerView >= 4 ? 1 : slidesPerView))
    const id = setInterval(() => {
      setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1))
    }, AUTOPLAY_INTERVAL_MS)
    return () => clearInterval(id)
  }, [slidesPerView, isAutoplayPaused])

  const handleGoPrev = useCallback(() => {
    goPrev()
    pauseAutoplayTemporarily()
  }, [goPrev, pauseAutoplayTemporarily])

  const handleGoNext = useCallback(() => {
    goNext()
    pauseAutoplayTemporarily()
  }, [goNext, pauseAutoplayTemporarily])

  const handleGoToSlide = useCallback((index: number) => {
    goToSlide(index)
    pauseAutoplayTemporarily()
  }, [goToSlide, pauseAutoplayTemporarily])

  const handleDragStartWithPause = useCallback(() => {
    isDraggingRef.current = true
    pauseAutoplayTemporarily()
  }, [pauseAutoplayTemporarily])

  return (
    <section id="work" className="bg-white py-32 border-t border-slate-200">
      <div className="container">
        <div className="mx-auto max-w-4xl text-center mb-24">
          <h2 className="text-sm font-bold text-accent uppercase tracking-[0.2em] mb-6">OUR WORK</h2>
          <h3 className="text-4xl md:text-7xl font-bold font-heading text-primary uppercase tracking-tight">
            Projects That <br/> <span className="text-accent">Deliver.</span>
          </h3>
        </div>

        {/* Slider - mobile & md only */}
        <div ref={containerRef} className="lg:hidden">
          {slidesPerView < 4 && (
            <>
              <div className="flex items-center justify-center gap-3 mb-6">
                <button
                  onClick={handleGoPrev}
                  disabled={currentSlide === 0}
                  className="flex items-center justify-center w-7 h-7 text-primary/60 hover:text-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300"
                  aria-label="Previous"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: maxSlide + 1 }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleGoToSlide(index)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        index === currentSlide ? 'bg-accent w-6' : 'bg-slate-300 w-1.5 hover:bg-slate-400'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={handleGoNext}
                  disabled={currentSlide === maxSlide}
                  className="flex items-center justify-center w-7 h-7 text-primary/60 hover:text-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300"
                  aria-label="Next"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
              <SliderSwipePrompt>
              <div className="relative overflow-hidden cursor-grab active:cursor-grabbing">
                <motion.div
                  className="relative z-10 flex"
                  style={{ x }}
                  drag="x"
                  dragConstraints={itemWidth > 0 ? { left: -maxSlide * itemWidth, right: 0 } : undefined}
                  dragElastic={0.15}
                  onDragStart={handleDragStartWithPause}
                  onDragEnd={handleDragEnd}
                  whileTap={{ cursor: 'grabbing' }}
                >
                  {allItems.map((entry, i) => (
                    <div
                      key={i}
                      className="flex-shrink-0 flex items-center justify-center p-4 sm:p-6 md:p-8 overflow-visible"
                      style={{ minWidth: itemWidth, width: itemWidth }}
                      onClick={() => { if (!justDragged && entry.type === 'card') setFlippedIndex(flippedIndex === i ? null : i) }}
                    >
                      <div className="w-full aspect-square max-w-[280px] sm:max-w-[340px] overflow-visible mx-auto">
                        {entry.type === 'card' ? (
                          <WorkCard item={entry.item} index={i} flippedIndex={flippedIndex} onFlip={setFlippedIndex} clickDisabled={justDragged} disableTilt />
                        ) : (
                          <div className="w-full h-full rounded-lg bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center">
                            <span className="text-slate-300/60 text-sm font-heading uppercase tracking-wide">Your business here</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
              </SliderSwipePrompt>
            </>
          )}
        </div>

        {/* Grid - large viewports only */}
        <div className="hidden lg:grid gap-8 grid-cols-3 max-w-5xl mx-auto w-full">
          {allItemsForGrid.map((entry, i) => (
            <div key={i} className="w-full">
              {entry.type === 'card' ? (
                <WorkCard item={entry.item} index={i} flippedIndex={flippedIndex} onFlip={setFlippedIndex} />
              ) : (
                <div className="w-full aspect-square rounded-lg bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center">
                  <span className="text-slate-300/60 text-sm font-heading uppercase tracking-wide">Your business here</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-20 text-center"
        >
          <Link
            to="/new-project"
            className="inline-flex items-center gap-2 text-lg font-bold font-heading text-accent hover:text-primary transition-colors group"
          >
            Your next project could be here. Get started
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
